/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

/* eslint-disable max-len */

import React, { FC } from 'react';

import TooltipOverParent from '@/components/sessionRecorder/TooltipOverParent';

export const HighlightVariables: FC<{
    text?: string;
    variables: { [key: string]: string };
    precomputedVariables: { [key: string]: string };
}> = ({ text = '', variables, precomputedVariables = {} }) => {
    const regex = /{{.*?}}/g;
    const parts = text.split(regex);
    const matches = [...text.matchAll(regex)].map((match) => match[0]);

    let matchIndex = 0;

    return (
        <span>
            {parts.map((part, index) => {
                const match = matches[matchIndex++];
                const variableKey = match?.slice(2, -2);
                const definition = variables[variableKey];
                const value = precomputedVariables[variableKey] ?? definition;
                return (
                    <VariableItem
                        key={index}
                        index={index}
                        part={part}
                        matches={matches}
                        definition={definition}
                        value={value}
                        match={match}
                    />
                );
            })}
        </span>
    );
};

export const VariableItem: FC<{
    index: number;
    part: string;
    matches: string[];
    definition: string;
    value: string;
    match: string;
}> = ({ index, part, matches, definition, value, match }) => {
    const trigger = (
        <span
            className="text-purple-400 cursor-help"
            aria-label={
                value ? `The variable ${match} is set to the value ${value}` : `The variable ${match} is not set`
            }
            {...(!value
                ? {
                      title: `The variable ${match} is not set`,
                  }
                : {})}
        >
            {match}
        </span>
    );
    return (
        <React.Fragment key={index}>
            {part}
            {index < matches.length && (
                <>
                    {definition ? (
                        <TooltipOverParent
                            tooltipPadding="p-0"
                            renderTooltip={() => (
                                <div className="min-w-[300px]">
                                    <div className="px-4 py-3 flex items-center text-slate-700 gap-1 border-b font-medium leading-6">
                                        <span className="rounded h-[24px] w-[24px] bg-purple-100 text-purple-700 flex items-center justify-center">
                                            <span className="text-xs">var</span>
                                        </span>
                                        {match}
                                    </div>
                                    <div className="py-1 px-2">
                                        <div className="font-medium leading-6">Definition:</div>
                                        <div className="">{definition}</div>
                                    </div>
                                    {definition !== value && (
                                        <div className="py-1 px-2">
                                            <div className="font-medium leading-6">Value:</div>
                                            <div className="">{value}</div>
                                        </div>
                                    )}
                                </div>
                            )}
                            renderTrigger={() => trigger}
                        />
                    ) : (
                        trigger
                    )}
                </>
            )}
        </React.Fragment>
    );
};
